<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TicketFormsAPI from 'dashboard/api/ticketForms';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  ticketCategories: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();

const formName = ref('');
const formType = ref('ticket');
const selectedCategoryId = ref('');
const selectedSubcategoryId = ref('');
const subcategories = ref([]);
const loadingSubcats = ref(false);
const isCreating = ref(false);

watch(selectedCategoryId, async newId => {
  selectedSubcategoryId.value = '';
  subcategories.value = [];
  if (!newId) return;
  loadingSubcats.value = true;
  try {
    const res = await TicketCategoriesAPI.getSubcategories(newId);
    const raw = res.data?.payload ?? res.data;
    subcategories.value = Array.isArray(raw) ? raw : [];
  } catch {
    subcategories.value = [];
  } finally {
    loadingSubcats.value = false;
  }
});

async function createForm() {
  const isTicket = formType.value === 'ticket';
  if (!formName.value.trim() || (isTicket && !selectedCategoryId.value)) return;

  isCreating.value = true;
  try {
    const payload = {
      name: formName.value.trim(),
      form_type: formType.value,
      active: true,
      form_options: {
        pages: [
          {
            title: t('FORM_BUILDER.PAGE.DEFAULT_TITLE'),
            description: '',
            nav_label: '',
            fields: [],
          },
        ],
      },
    };
    if (isTicket) {
      payload.ticket_category_id = selectedCategoryId.value;
      if (selectedSubcategoryId.value) {
        payload.default_subcategory_id = selectedSubcategoryId.value;
      }
    } else {
      payload.confirmation_message = t(
        'FORM_BUILDER.NEW_FORM_MODAL.CONTACT_CONFIRMATION'
      );
    }
    const { data } = await TicketFormsAPI.create({ ticket_form: payload });
    emit('created', data);
  } catch {
    useAlert(t('FORMS_SETTINGS.ALERTS.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
}
</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    @click.self="$emit('close')"
  >
    <div
      class="bg-n-background rounded-xl border border-n-weak shadow-xl w-full max-w-md overflow-hidden"
    >
      <div class="px-5 py-4 border-b border-n-weak">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('FORMS_SETTINGS.CREATE_MODAL.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-10 mt-0.5">
          {{ $t('FORMS_SETTINGS.CREATE_MODAL.DESCRIPTION') }}
        </p>
      </div>
      <div class="px-5 py-4 space-y-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('FORMS_SETTINGS.CREATE_MODAL.NAME_LABEL') }}
          </label>
          <input
            v-model="formName"
            type="text"
            :placeholder="$t('FORMS_SETTINGS.CREATE_MODAL.NAME_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 placeholder-n-slate-9"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_LABEL') }}
          </label>
          <select
            v-model="formType"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
          >
            <option value="ticket">
              {{ $t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_TICKET') }}
            </option>
            <option value="contact">
              {{ $t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_CONTACT') }}
            </option>
          </select>
          <span class="text-xs text-n-slate-10">
            {{ $t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_HINT') }}
          </span>
        </div>
        <div v-if="formType === 'ticket'" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('FORMS_SETTINGS.CREATE_MODAL.CATEGORY_LABEL') }}
          </label>
          <select
            v-model="selectedCategoryId"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
          >
            <option value="" disabled>
              {{ $t('FORMS_SETTINGS.CREATE_MODAL.CATEGORY_PLACEHOLDER') }}
            </option>
            <option
              v-for="cat in ticketCategories"
              :key="cat.id"
              :value="cat.id"
            >
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              {{ cat.name }} ({{ cat.code }})
            </option>
          </select>
        </div>
        <div
          v-if="formType === 'ticket' && selectedCategoryId"
          class="flex flex-col gap-1"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('FORMS_SETTINGS.CREATE_MODAL.SUBCATEGORY_LABEL') }}
          </label>
          <select
            v-model="selectedSubcategoryId"
            :disabled="loadingSubcats"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 disabled:opacity-50"
          >
            <option value="">
              {{ $t('FORMS_SETTINGS.CREATE_MODAL.SUBCATEGORY_PLACEHOLDER') }}
            </option>
            <option v-for="sub in subcategories" :key="sub.id" :value="sub.id">
              {{ sub.name }}
            </option>
          </select>
        </div>
      </div>
      <div
        class="px-5 py-3 border-t border-n-weak flex items-center justify-end gap-2"
      >
        <button
          class="px-4 py-2 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-2"
          @click="$emit('close')"
        >
          {{ $t('FORMS_SETTINGS.CREATE_MODAL.CANCEL') }}
        </button>
        <Button
          :label="$t('FORMS_SETTINGS.CREATE_MODAL.CREATE')"
          color="blue"
          :is-loading="isCreating"
          :disabled="
            !formName.trim() || (formType === 'ticket' && !selectedCategoryId)
          "
          @click="createForm"
        />
      </div>
    </div>
  </div>
</template>
